// Gallery content (photos) data access against admin.api.photohawk.com.
// Mirrors photohawk-admin's `actions/content.js` search():
//   GET /admin/content?engineGuid=&slug=&text=&format=jpg -> { hits: [...] }
// Each hit has an `image.thumbnail` URL. (The legacy client also measures image
// dimensions on the client via getImgSize; we let the browser size thumbnails.)

import { useQuery } from "@tanstack/react-query";
import { photohawkApi, PhotohawkApiError, isTransientApiError } from "@/lib/photohawkApi";
import { getApiBaseDomain, tenantHostname } from "@/lib/apiDomain";
import type { Content } from "@/types/photohawk";
import type { TenantScope } from "@/lib/api/galleries";

export interface ContentQuery {
  engineGuid?: string;
  slug?: string;
  text?: string;
  /** Exact filename list (matches any) — e.g. an order's photos. Indexed exact lookup. */
  names?: string[];
  /** Typed filename search terms (matches any, case-insensitive): a term without `*` matches
   *  filenames CONTAINING it ("John_Doe"); `*` is a wildcard ("John_Doe*", "*_DSC*.jpg"). ORed
   *  with `names`. Admin reads only (api-admin nameSearch). */
  nameSearch?: string[];
  /** Filter to one or more collection (folder) guids. */
  collections?: string[];
  /** Face IDs from a selfie search (POST /galleries/searchByImage). */
  faces?: string[];
  /** Cache key returned by searchByImage when there are many faces. */
  requestId?: string;
  /** 1-based page (the admin read returns 100/page; query() maps page→offset). */
  page?: number;
  /** Opaque cursor (from a prior result's nextCursor) to page past the 10k window. */
  cursor?: string;
  /** Hits per page. Server default is 100; server clamps to ≤1000. The scan loader raises it. */
  size?: number;
  /** Response projection. "scan" returns the lean per-hit shape (metadata + a single
   * previewBase URL) for the burst-scan loader; omit for the full shape. */
  projection?: "scan";
}

interface ContentResponse {
  hits?: Content[];
  total?: number;
  /** Opaque next-page cursor (search_after), present while more pages remain. */
  cursor?: string | null;
  /** The caller only sees photos credited to them (lib/api/ownPhotos). */
  ownPhotosOnly?: boolean;
  /** …and `total` is theirs (the gallery's index carries the uploader). */
  ownTotalExact?: boolean;
}

export interface ContentResult {
  hits: Content[];
  total: number;
  /** Opaque cursor for the next page, or null/undefined at the end. */
  nextCursor?: string | null;
  /** "Photographers see only their own photos" is on for the caller: `hits` are only theirs. */
  ownPhotosOnly?: boolean;
  /** `total` and paging are theirs too. Without it (a gallery whose index hasn't been rebuilt with the uploader yet)
   *  `total` is the gallery's — it drives the paging, so pages can be short — and mustn't be shown as their count. */
  ownTotalExact?: boolean;
}

export const listContent = async (
  scope: TenantScope,
  query: ContentQuery,
  signal?: AbortSignal,
): Promise<ContentResult> => {
  const res = await photohawkApi.get<ContentResponse>("/admin/content", {
    service: "admin",
    hostname: tenantHostname(scope),
    signal,
    params: {
      engineGuid: query.engineGuid,
      slug: query.slug,
      text: query.text || undefined,
      names:
        query.names && query.names.length > 0 ? JSON.stringify(query.names) : undefined,
      nameSearch:
        query.nameSearch && query.nameSearch.length > 0 ? JSON.stringify(query.nameSearch) : undefined,
      collections:
        query.collections && query.collections.length > 0
          ? JSON.stringify(query.collections)
          : undefined,
      faces:
        query.faces && query.faces.length > 0 ? JSON.stringify(query.faces) : undefined,
      requestId: query.requestId || undefined,
      page: query.page && query.page > 1 ? query.page : undefined,
      cursor: query.cursor || undefined,
      size: query.size && query.size > 0 ? query.size : undefined,
      projection: query.projection || undefined,
      format: "jpg",
    },
  });
  const hits = res?.hits ?? [];
  return { hits, total: res?.total ?? hits.length, nextCursor: res?.cursor ?? null, ...(res?.ownPhotosOnly ? { ownPhotosOnly: true } : {}), ...(res?.ownTotalExact ? { ownTotalExact: true } : {}) };
};

// --- Upload ----------------------------------------------------------------
// Mirrors photohawk-admin's beginUpload(): POST /admin/content returns presigned
// S3 POSTs (one per file, index-aligned), then each file is uploaded DIRECTLY to
// S3 as multipart/form-data. We send file metadata (name/type/size) rather than
// raw File objects (which don't JSON-serialise) — see staging-validation.md.

/** A presigned PUT target for an on-device scan artifact (crop image / sidecar). */
export interface PrescanTarget {
  key: string;
  url: string;
}

/**
 * On-device pre-scan targets the backend mints alongside the plain content POST
 * (only when the client asked for `prescan` and the server's R2/results wiring
 * is present). Mirrors api-admin/content/create.js.
 */
export interface PrescanTargets {
  contentGuid?: string;
  /** The prescanned content POST fields — swap to these only if every artifact lands. */
  content?: { fields: Record<string, string> };
  /** Sidecar JSON PUT → scanning-results bucket. */
  sidecar?: PrescanTarget;
  faceCrops?: PrescanTarget[];
  personCrops?: PrescanTarget[];
}

export interface PresignedPost {
  /** The actual S3 POST endpoint (the API calls this `directUrl`; `url` is the CDN). */
  directUrl?: string;
  url?: string;
  /** Present for the S3 POST-form path; absent for the R2 PUT path below. */
  fields?: Record<string, string>;
  /**
   * Egress Phase 2 — R2 target. Cloudflare R2 has no S3 POST Object API, so a mint that
   * targets R2 returns a single presigned PUT here instead of `fields`. The client PUTs
   * the raw file to `url` echoing exactly these (signed) `x-amz-meta-*` headers.
   */
  put?: { url: string; headers?: Record<string, string> };
  /**
   * Upload Worker target (photos into R2, when the stage offers it and we asked with
   * `uploadVia: 'worker'`): PUT the raw file to `url` with the `x-ph-token` header. It goes
   * through Cloudflare's edge (HTTP/2+3, one cached CORS preflight) instead of R2's HTTP/1.1
   * endpoint, which caps a browser at 6 uploads at once. `put` stays the fallback.
   */
  worker?: { url: string; token: string };
  /** Present only when on-device prescanning was requested and is available. */
  prescan?: PrescanTargets | null;
}

/** Per-file scan metadata sent with a prescan presign request. */
export interface ScanFileMeta {
  path: string;
  scanned: boolean;
  faceCount: number;
  personCount: number;
}

interface UploadUrlsResponse {
  upload?: { urls?: PresignedPost[]; prescanAvailable?: boolean };
}

interface AssessResponse {
  keys?: string[];
  /** Folder view only (`delimiter: true`): subfolder paths at this level. */
  folders?: string[];
  nextToken?: string | null;
}

/** One level of a collection, FTP-style: its subfolders and the files directly in it. */
export interface FolderLevel {
  /** Subfolder paths relative to the collection (e.g. "DD/100EOS1D"). */
  folders: string[];
  /** File keys relative to the collection (e.g. "DD/100EOS1D/A56I2258.JPG"). */
  keys: string[];
}

/** List one level under `sub` ("" = the collection root) — subfolders + files — from storage,
 *  the same place FTP lists. Paged server-side (1,000 per request). */
export const listFolderLevel = async (
  scope: TenantScope,
  engineGuid: string,
  collectionGuid: string,
  sub: string,
  signal?: AbortSignal,
): Promise<FolderLevel> => {
  const folders: string[] = [];
  const keys: string[] = [];
  let nextToken: string | null = null;
  let guard = 0;
  do {
    const res: AssessResponse = await photohawkApi.post<AssessResponse>("/admin/content", {
      service: "admin",
      hostname: tenantHostname(scope),
      signal,
      body: { assess: true, delimiter: true, subPrefix: sub, engineGuid, collectionGuid, continuationToken: nextToken },
    });
    folders.push(...(res?.folders || []));
    keys.push(...(res?.keys || []));
    nextToken = res?.nextToken || null;
    guard += 1;
  } while (nextToken && guard < 500);
  return { folders, keys };
};

export const useFolderLevel = (
  scope: TenantScope,
  engineGuid: string | undefined,
  collectionGuid: string | undefined,
  sub: string,
  opts: { enabled?: boolean; refetchInterval?: number | false } = {},
) =>
  useQuery({
    queryKey: ["folder-level", scope.alias ?? scope.customDomain ?? "current", engineGuid ?? "", collectionGuid ?? "", sub],
    queryFn: ({ signal }) => listFolderLevel(scope, engineGuid!, collectionGuid!, sub, signal),
    enabled: (opts.enabled ?? true) && !!engineGuid && !!collectionGuid,
    refetchInterval: opts.refetchInterval,
    staleTime: 5_000,
  });

/**
 * Pre-flight duplicate check. POST /admin/content with `assess:true` pages the
 * S3 keys already present under this collection's prefix (mirrors legacy
 * listExistingKeys), so the uploader can skip (default) or overwrite files that
 * already exist — which also gives free resumability of an interrupted batch.
 */
export const listExistingKeys = async (
  scope: TenantScope,
  engineGuid: string,
  collectionGuid?: string,
): Promise<Set<string>> => {
  const existing = new Set<string>();
  let nextToken: string | null = null;
  let guard = 0; // hard page cap so a pathological listing can't loop forever
  do {
    const res: AssessResponse = await photohawkApi.post<AssessResponse>("/admin/content", {
      service: "admin",
      hostname: tenantHostname(scope),
      body: { assess: true, engineGuid, collectionGuid, continuationToken: nextToken },
    });
    for (const k of res?.keys || []) existing.add(k);
    nextToken = res?.nextToken || null;
    guard += 1;
  } while (nextToken && guard < 2000);
  return existing;
};

/** One photo that went live from its quick copy (preview-first) and still needs its original. */
export interface PendingOriginal {
  /** Path relative to the collection — the same form as an upload path / listExistingKeys key. */
  key: string;
  collectionGuid: string;
  /** Someone already bought it — its original is the most urgent (the server lists these first). */
  sold: boolean;
}

/** The uploader's API calls (mints, owed / sold lists, crop-back polls) give up after this long — the admin API
 *  answers within API Gateway's 29 s or not at all, so a request still open after a minute is a dead connection,
 *  and the caller retries (a fetch has no timeout of its own; see photohawkApi `timeoutMs`). */
export const UPLOADER_API_TIMEOUT_MS = 60_000;

/**
 * Preview-first: which photos in this gallery (optionally one collection) are still live from
 * a quick copy and owed their original. POST /admin/content {pendingOriginals:true}. Any
 * failure reads as "unknown" (null) — callers then simply don't offer to finish originals.
 */
export const listPendingOriginals = async (
  scope: TenantScope,
  engineGuid: string,
  collectionGuid?: string,
): Promise<PendingOriginal[] | null> => {
  try {
    const res = await photohawkApi.post<{ pending?: PendingOriginal[] }>("/admin/content", {
      service: "admin",
      hostname: tenantHostname(scope),
      body: { pendingOriginals: true, engineGuid, collectionGuid },
      timeoutMs: UPLOADER_API_TIMEOUT_MS,
    });
    return Array.isArray(res?.pending) ? res.pending : null;
  } catch {
    return null;
  }
};

/**
 * Preview-first: just the SOLD photos in this gallery (optionally one collection) still owed their original —
 * a buyer is waiting on them. POST /admin/content {pendingOriginals:true, soldOnly:true} reads a small sparse
 * index, so the uploader can ask every few seconds while it uploads. Throws on a network/API error (the
 * poller backs off); an older backend without the index answers the full list, which is filtered here.
 */
export const listSoldOriginals = async (
  scope: TenantScope,
  engineGuid: string,
  opts: { collectionGuid?: string; full?: boolean; signal?: AbortSignal } = {},
): Promise<{ sold: Array<{ key: string; collectionGuid: string; soldAt?: number | null }>; pollMs?: number; sparse: boolean }> => {
  const res = await photohawkApi.post<{ pending?: Array<PendingOriginal & { soldAt?: number | null }>; pollMs?: number; soldOnly?: boolean }>(
    "/admin/content",
    {
      service: "admin",
      hostname: tenantHostname(scope),
      body: { pendingOriginals: true, soldOnly: !opts.full, engineGuid, collectionGuid: opts.collectionGuid },
      signal: opts.signal,
      timeoutMs: UPLOADER_API_TIMEOUT_MS,
    },
  );
  const list = Array.isArray(res?.pending) ? res.pending : [];
  return {
    sold: list.filter((p) => p && p.sold).map((p) => ({ key: p.key, collectionGuid: p.collectionGuid, soldAt: p.soldAt ?? null })),
    pollMs: typeof res?.pollMs === "number" ? res.pollMs : undefined,
    // false: the backend ignored soldOnly and answered the full owed list (it predates the sparse index).
    sparse: res?.soldOnly === true,
  };
};

/**
 * Crop-back poll + report (photohawk-serverless lib cropBack.js): one call reports the crops uploaded
 * since the last poll and returns, for up to 100 preview copies, the crops the server wants cut from
 * their originals (src/lib/uploader/cropBack.ts drives it). Throws on a network/API error — the caller
 * retries on its next cycle.
 */
export const pollCropBack = async (
  scope: TenantScope,
  engineGuid: string,
  poll: string[],
  done: Array<{ contentGuid: string; faces?: "ok" | "skip"; people?: "ok" | "skip" }>,
  viaWorker = false,
): Promise<import("@/lib/uploader/cropBack").PollResponse> =>
  photohawkApi.post("/admin/content", {
    service: "admin",
    hostname: tenantHostname(scope),
    // viaWorker: each crop also gets an upload-Worker token (and the bundle endpoint comes back).
    body: { engineGuid, cropBack: { poll, done }, ...(viaWorker ? { uploadVia: "worker" } : {}) },
    timeoutMs: UPLOADER_API_TIMEOUT_MS,
  });

// Basename index per key set, built once on first lookup. The old per-call scan of every
// key made a pre-flight / resume check O(files x keys) — billions of string compares for a
// 100k batch against a large collection, freezing the tab for minutes. Key sets from
// listExistingKeys are never mutated after they're returned, so caching by identity is safe.
const basenameIndex = new WeakMap<Set<string>, Set<string>>();
const basenamesOf = (existing: Set<string>): Set<string> => {
  let idx = basenameIndex.get(existing);
  if (!idx) {
    idx = new Set<string>();
    for (const k of existing) idx.add(k.slice(k.lastIndexOf("/") + 1));
    basenameIndex.set(existing, idx);
  }
  return idx;
};

/** True when a local file's name already exists in the pre-flight key set. Keys may carry a
 *  prefix (subfolder/…), so a bare name also matches on the key's trailing name; a name that
 *  itself carries a path (folder-preserving upload) must match that exact key (keys are
 *  relative to the collection, the same form the upload path takes). */
export const keyExists = (existing: Set<string>, fileName: string): boolean => {
  if (existing.has(fileName)) return true;
  if (fileName.includes("/")) return false;
  return basenamesOf(existing).has(fileName);
};

/**
 * Presign a batch of uploads. When `scanMeta` is supplied the request asks the
 * backend to additionally mint on-device prescan targets (crop + sidecar PUTs
 * and a prescanned content POST) per file; `prescanAvailable` reports whether it
 * actually did (it silently declines if the R2/results wiring isn't present, so
 * the client just uploads plain and the server scans as before).
 */
/**
 * A file's path relative to the picked folder (for directory uploads), else its
 * name. The backend keys the S3 object off `<tenant>/<collection>/<path>`, so
 * returning the relative path mirrors any sub-folder structure into the prefix.
 */
export const relativePath = (f: File): string =>
  (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;

export const requestUploadUrls = async (
  scope: TenantScope,
  engineGuid: string,
  files: File[],
  collectionGuid?: string,
  scanMeta?: ScanFileMeta[],
): Promise<{ urls: PresignedPost[]; prescanAvailable: boolean }> => {
  const res = await photohawkApi.post<UploadUrlsResponse>("/admin/content", {
    service: "admin",
    hostname: tenantHostname(scope),
    body: {
      engineGuid,
      // The backend writes each object under this collection's prefix.
      collectionGuid,
      prescan: !!scanMeta,
      // The backend keys each presigned upload off `file.path` (relative path →
      // sub-folders are mirrored into the key); when prescanning it also reads
      // `scanned` / `faceCount` / `personCount`.
      files: scanMeta ?? files.map((f) => ({ path: relativePath(f) })),
    },
  });
  return {
    urls: res?.upload?.urls ?? [],
    prescanAvailable: res?.upload?.prescanAvailable === true,
  };
};

/**
 * PUT one scan artifact (a crop image blob or the sidecar JSON) to its presigned
 * target. Best-effort — resolves `false` on any failure so the caller falls back
 * to a plain (non-prescanned) upload.
 */
export const putArtifact = async (
  url: string,
  body: Blob | string,
  contentType: string,
  signal?: AbortSignal,
): Promise<boolean> => {
  try {
    const res = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body,
      signal,
    });
    return res.ok;
  } catch {
    return false;
  }
};

// ---- Stalled uploads -----------------------------------------------------------
// An XHR has no timeout of its own, and a socket can stall without ever erroring — a half-open connection
// after a dropped Wi-Fi, a laptop that slept, a proxy that stopped forwarding. One such upload never settles,
// and the pass waiting on it never finishes (andrewswalesphotography, 18 Oct 2026: an originals pass sat at
// 4,928 / 5,143 for hours on ONE upload that never answered). So every upload is watched: no bytes sent and no
// answer for UPLOAD_STALL_MS → it's aborted and fails as "stalled", which the caller retries like any dropped
// connection. A slow upload is never cut off — any progress event re-arms the timer.
export const UPLOAD_STALL_MS = 90_000;
let uploadStallMs = UPLOAD_STALL_MS;
/** Test hook: shorten (or restore, with no argument) the stall timeout. */
export const setUploadStallMs = (ms = UPLOAD_STALL_MS) => { uploadStallMs = ms; };

/** The error a stalled upload fails with (uploadErrorName reads it as "stalled"). */
export class UploadStalledError extends Error {
  constructor(seconds: number) {
    super(`Upload stalled (no progress for ${seconds} s)`);
    this.name = "UploadStalledError";
  }
}

/**
 * Watch one XHR: `fail` is called with an UploadStalledError (then the request is aborted) if neither
 * `poke()` (progress) nor `stop()` (it settled) happens for the stall timeout. Progress on the upload, the
 * end of the body and the response all count.
 */
export const watchStall = (xhr: XMLHttpRequest, fail: (err: Error) => void): { poke: () => void; stop: () => void } => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let done = false;
  const stop = () => {
    done = true;
    if (timer != null) clearTimeout(timer);
    timer = null;
  };
  const poke = () => {
    if (done) return;
    if (timer != null) clearTimeout(timer);
    timer = setTimeout(() => {
      if (done) return;
      stop();
      fail(new UploadStalledError(Math.round(uploadStallMs / 1000))); // first, so the abort's own reject is a no-op
      try { xhr.abort(); } catch { /* already finished */ }
    }, uploadStallMs);
  };
  try {
    xhr.upload?.addEventListener?.("load", poke);
    xhr.addEventListener?.("progress", poke);
    xhr.addEventListener?.("loadend", stop);
  } catch { /* a minimal XHR (tests) — the caller's own progress handler still pokes */ }
  poke();
  return { poke, stop };
};

/**
 * Upload one file to its presigned S3 POST target. Direct to S3 (no API auth
 * header), via XHR so we can report progress. Resolves on a 2xx response.
 *
 * When an `AbortSignal` is supplied the in-flight PUT is cancelled if the signal
 * fires (used by the upload manager's cancel), rejecting with an `AbortError`.
 */
export const uploadToPresigned = (
  post: PresignedPost,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }

    const form = new FormData();
    Object.entries(post.fields || {}).forEach(([k, v]) => form.append(k, v));
    form.append("file", file); // S3 requires the file field last

    const target = post.directUrl || post.url;
    if (!target) {
      reject(new Error("No upload URL"));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open("POST", target, true);
    const onAbort = () => xhr.abort();
    if (signal) signal.addEventListener("abort", onAbort);
    const stall = watchStall(xhr, (err) => { cleanup(); reject(err); });
    const cleanup = () => {
      stall.stop();
      if (signal) signal.removeEventListener("abort", onAbort);
    };
    xhr.upload.onprogress = (e) => {
      stall.poke();
      if (onProgress && e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error("Network error during upload"));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };
    xhr.send(form);
  });

/**
 * Egress Phase 2 — R2 target. Upload one file with a single presigned PUT (R2 has no
 * S3 POST Object API, so the form-POST path above can't target it). Echoes the exact
 * `x-amz-meta-*` headers the mint signed, streams the raw file body, reports progress
 * via XHR, and honours an AbortSignal. Resolves on a 2xx — same contract as
 * uploadToPresigned, so the upload manager's retry/re-mint logic is identical.
 */
export const uploadPutPresigned = (
  put: { url: string; headers?: Record<string, string> },
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    if (!put?.url) {
      reject(new Error("No upload URL"));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open("PUT", put.url, true);
    // Only the mint's signed headers — Content-Type stays unsigned (the browser sets it
    // from the File), so it can't break the SigV4 signature.
    Object.entries(put.headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    const onAbort = () => xhr.abort();
    if (signal) signal.addEventListener("abort", onAbort);
    const stall = watchStall(xhr, (err) => { cleanup(); reject(err); });
    const cleanup = () => {
      stall.stop();
      if (signal) signal.removeEventListener("abort", onAbort);
    };
    xhr.upload.onprogress = (e) => {
      stall.poke();
      if (onProgress && e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error("Network error during upload"));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };
    xhr.send(file);
  });

// ---- Upload Worker ------------------------------------------------------------
// On by default wherever the mint offers it; `localStorage ph_upload_worker=0` turns it off.
// After WORKER_MAX_FAILS failures in a row the Worker is shelved and uploads go direct, so a
// broken Worker costs a few retries, not every upload. But the trip is NOT permanent for the
// session: the common failure is a transient transport error (a flaky path to the edge —
// e.g. ERR_SSL_BAD_RECORD_MAC_ALERT — or a VPN/proxy mangling TLS), which clears on its own.
// So after WORKER_COOLDOWN_MS we let the Worker be tried again; one success re-enables it fully,
// another failure re-arms the cooldown. Without this a brief blip disabled the Worker for the
// whole session even once the network recovered.
const WORKER_MAX_FAILS = 3;
export const WORKER_COOLDOWN_MS = 60_000;
let workerFails = 0;
let workerRetryAt = 0; // when a shelved Worker may be probed again (epoch ms); 0 = not shelved

export const uploadWorkerWanted = (): boolean => {
  try {
    if (globalThis.localStorage?.getItem("ph_upload_worker") === "0") return false;
  } catch {
    /* localStorage unavailable — Worker stays on by default */
  }
  if (workerFails < WORKER_MAX_FAILS) return true;
  // Shelved after repeated failures — allow a re-probe once the cooldown has passed.
  return Date.now() >= workerRetryAt;
};

export const noteUploadWorker = (ok: boolean) => {
  if (ok) {
    if (workerFails >= WORKER_MAX_FAILS) console.info("[upload] upload Worker recovered — using it again");
    workerFails = 0;
    workerRetryAt = 0;
    return;
  }
  workerFails += 1;
  if (workerFails >= WORKER_MAX_FAILS) {
    workerRetryAt = Date.now() + WORKER_COOLDOWN_MS;
    if (workerFails === WORKER_MAX_FAILS) console.warn(`[upload] upload Worker failing — using direct uploads; will re-probe in ${WORKER_COOLDOWN_MS / 1000}s`);
  }
};

/** Test hook. */
export const resetUploadWorker = () => { workerFails = 0; workerRetryAt = 0; };

/** Stop using the upload Worker for a while (its probe failed in this browser): uploads go direct. */
export const shelveUploadWorker = () => {
  workerFails = Math.max(workerFails, WORKER_MAX_FAILS);
  workerRetryAt = Date.now() + WORKER_COOLDOWN_MS;
};

/** The upload Worker's object endpoint for this stage (the mint hands out the same URL). */
export const uploadWorkerObjectUrl = (domain = getApiBaseDomain()): string =>
  domain === "photohawk-staging.com" ? "https://up-staging.photohawk-cdn.com/o" : "https://up.photohawk-cdn.com/o";

/**
 * Can this browser reach the upload Worker the way a copy upload does — an XHR PUT with the `x-ph-token`
 * header (so a CORS preflight) and a Blob body? A deliberately invalid token: the Worker answers 403
 * `bad_or_expired_token` and stores nothing. Any other outcome (a network error, no CORS) = no.
 */
export const probeUploadWorker = (url = uploadWorkerObjectUrl(), timeoutMs = 8000): Promise<{ ok: boolean; detail: string }> =>
  new Promise((resolve) => {
    let xhr: XMLHttpRequest;
    try {
      xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      xhr.setRequestHeader("x-ph-token", "probe");
      xhr.timeout = timeoutMs;
    } catch (e) {
      resolve({ ok: false, detail: e instanceof Error ? e.message : String(e) });
      return;
    }
    xhr.upload.onprogress = () => {}; // like a real upload (a progress listener is part of the request)
    xhr.onload = () => {
      const ok = xhr.status === 403 && /bad_or_expired_token/.test(xhr.responseText || "");
      resolve({ ok, detail: `HTTP ${xhr.status}` });
    };
    xhr.onerror = () => resolve({ ok: false, detail: "network error" });
    xhr.ontimeout = () => resolve({ ok: false, detail: "timed out" });
    xhr.send(new Blob([new Uint8Array(16)], { type: "image/jpeg" }));
  });

export const uploadViaWorker = (
  worker: { url: string; token: string },
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> => uploadPutPresigned({ url: worker.url, headers: { "x-ph-token": worker.token } }, file, onProgress, signal);

// ---- Upload Worker bundles (POST /b) ------------------------------------------
// Many small files per request (preview-first copies, crop-back crops): the bundle is a stream of
// length-prefixed entries — "PHB1", then per entry a u32 big-endian header length, the header JSON
// {t: token, n: bytes, ct}, and the bytes — built as a Blob of parts, so file bytes are never copied.
// The Worker stores each entry exactly like a single PUT and acks each one; the caller re-sends only
// the entries that weren't acked (on their single-file path, with its own fallbacks).
export interface BundleTarget {
  url: string;
  maxEntries: number;
  maxBytes: number;
}
export interface BundleEntry {
  token: string;
  blob: Blob;
  contentType?: string;
}

/** Bundled Worker uploads: OFF unless `localStorage ph_up_bundle=1` (see bench-upload-bundle-staging). */
export const uploadBundleWanted = (): boolean => {
  try {
    return globalThis.localStorage?.getItem("ph_up_bundle") === "1";
  } catch {
    return false;
  }
};

const BUNDLE_MAGIC = new Uint8Array([0x50, 0x48, 0x42, 0x31]); // "PHB1"

/** The bundle body for `entries`, and where each entry's bytes start in it (for progress). */
export const bundleBody = (entries: BundleEntry[]): { body: Blob; offsets: number[] } => {
  const enc = new TextEncoder();
  const parts: BlobPart[] = [BUNDLE_MAGIC];
  const offsets: number[] = [];
  let at = BUNDLE_MAGIC.length;
  for (const e of entries) {
    const head = enc.encode(JSON.stringify({ t: e.token, n: e.blob.size, ct: e.contentType || "image/jpeg" }));
    const len = new Uint8Array(4);
    new DataView(len.buffer).setUint32(0, head.length);
    parts.push(len, head, e.blob);
    at += 4 + head.length;
    offsets.push(at);
    at += e.blob.size;
  }
  return { body: new Blob(parts, { type: "application/x-ph-bundle" }), offsets };
};

/**
 * Upload a bundle. Resolves with one boolean per entry (acked by the Worker); rejects on a network
 * error / non-200 (every entry then counts as not acked). `onProgress(bytesOfEntry[])` reports how much
 * of each entry has been sent so far.
 */
export const uploadBundle = (
  target: BundleTarget,
  entries: BundleEntry[],
  onProgress?: (sent: number[]) => void,
  signal?: AbortSignal,
): Promise<boolean[]> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const { body, offsets } = bundleBody(entries);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", target.url, true);
    xhr.setRequestHeader("Content-Type", "application/x-ph-bundle");
    const onAbort = () => xhr.abort();
    if (signal) signal.addEventListener("abort", onAbort);
    const stall = watchStall(xhr, (err) => { cleanup(); reject(err); });
    const cleanup = () => {
      stall.stop();
      if (signal) signal.removeEventListener("abort", onAbort);
    };
    xhr.upload.onprogress = (e) => {
      stall.poke();
      if (!onProgress) return;
      onProgress(entries.map((en, k) => Math.max(0, Math.min(en.blob.size, e.loaded - offsets[k]))));
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status !== 200) {
        reject(new Error(`Bundle upload failed (${xhr.status})`));
        return;
      }
      try {
        const res = JSON.parse(xhr.responseText || "{}") as { results?: Array<{ i: number; ok: boolean }> };
        const acked = entries.map(() => false);
        for (const r of res.results || []) if (r && r.ok && r.i >= 0 && r.i < acked.length) acked[r.i] = true;
        resolve(acked);
      } catch {
        resolve(entries.map(() => false));
      }
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error("Network error during bundle upload"));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };
    xhr.send(body);
  });

// ---- S3 multipart (large files, e.g. big videos) ----------------------------
// The mint returns a `multipart` plan (presigned uploadPart PUTs + a
// completeMultipartUpload POST + an abortMultipartUpload DELETE) when a file is
// requested with partCount > 1 AND the mint body says `multipart: "v2"` (the server
// keeps every other client — the Studio app — on a single POST). The client PUTs each
// part, collects its ETag, and POSTs the completion XML. The content bucket's CORS
// allows any origin and exposes `ETag` (staging + prod, checked 20 Oct 2026), and the
// staging e2e (photohawk-serverless e2e-videos-staging) completes a real upload this way.
// ON for videos by default; `localStorage.ph_multipart = "0"` turns it off (single POST).
export interface MultipartPlan {
  uploadId: string;
  parts: { url: string; headers?: Record<string, string> }[];
  complete: { url: string; headers?: Record<string, string> };
  abort: { url: string; headers?: Record<string, string> };
}

const MULTIPART_PART_SIZE = 32 * 1024 * 1024; // 32 MB per part
const MULTIPART_THRESHOLD = 64 * 1024 * 1024; // only files above this go multipart
const MULTIPART_CONCURRENCY = 4; // parts uploaded in parallel (per file)
// Part PUTs in flight across EVERY multipart file at once. Chrome opens at most 6 HTTP/1.1 connections to the
// bucket host; any XHR beyond that queues inside the browser with its stall watchdog already running, so a few
// videos x 4 parts used to queue 20+ PUTs, the queued ones "stalled" at 90 s without ever starting, burned their
// retries and the clip failed (Shutter Sports, 26 Oct / 1 Nov: 0-8 of 150-300 clips). Waiting happens here, in
// JS, before an XHR exists — under the socket limit, with room left for the API calls the uploader also makes.
export const MULTIPART_GLOBAL_PARTS = 4;
let partSlots = MULTIPART_GLOBAL_PARTS;
const partWaiters: Array<() => void> = [];
const acquirePartSlot = async (): Promise<() => void> => {
  if (partSlots > 0) {
    partSlots -= 1;
  } else {
    await new Promise<void>((resolve) => partWaiters.push(resolve));
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const next = partWaiters.shift();
    if (next) next();
    else partSlots += 1;
  };
};
/** Tests: reset the shared part limiter. */
export const resetPartSlots = (n = MULTIPART_GLOBAL_PARTS): void => {
  partSlots = n;
  partWaiters.length = 0;
};

/** S3's error code from an error body (`<Code>ExpiredToken</Code>`), or "". */
const s3Code = (body: string | null | undefined): string => {
  const m = /<Code>([^<]+)<\/Code>/.exec(body || "");
  return m ? m[1] : "";
};
// A multi-GB clip has dozens of parts — give each a few more tries (backoff 0.5 s .. 8 s) so one
// flaky part doesn't throw the whole upload away.
const MULTIPART_PART_ATTEMPTS = 5;

/** Videos upload as S3 multipart (per-part retry + progress). Kill switch: localStorage ph_multipart=0. */
export const multipartEnabled = (): boolean => {
  try {
    return typeof localStorage === "undefined" || localStorage.getItem("ph_multipart") !== "0";
  } catch {
    return true;
  }
};

/** Parts for a file, or 0 to use a single POST. Clamped to S3's 10,000-part ceiling. */
export const multipartPartCount = (size: number): number => {
  if (!Number.isFinite(size) || size < MULTIPART_THRESHOLD) return 0;
  return Math.max(2, Math.min(10000, Math.ceil(size / MULTIPART_PART_SIZE)));
};

const putPart = (
  url: string,
  headers: Record<string, string> | undefined,
  blob: Blob,
  onLoaded: (loaded: number) => void,
  register: (xhr: XMLHttpRequest) => void,
): Promise<string> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    register(xhr);
    const stall = watchStall(xhr, reject);
    xhr.upload.onprogress = (e) => {
      stall.poke();
      if (e.lengthComputable) onLoaded(e.loaded);
    };
    xhr.onload = () => {
      stall.stop();
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader("ETag");
        onLoaded(blob.size);
        if (!etag) {
          reject(new Error("Multipart part had no ETag — the bucket CORS must expose the ETag header"));
        } else {
          resolve(etag);
        }
      } else {
        let body = "";
        try { body = typeof xhr.responseText === "string" ? xhr.responseText : ""; } catch { /* not readable */ }
        const code = s3Code(body);
        reject(new Error(`Multipart part failed (${xhr.status})${code ? ` ${code}` : ""}`));
      }
    };
    xhr.onerror = () => reject(new Error("Network error during multipart part"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    xhr.send(blob);
  });

/**
 * Upload a file to S3 as multipart, following the mint's plan: PUT each part
 * (bounded parallelism, per-part retry), then POST the completion XML. Aborts the
 * whole multipart upload (best-effort) on cancel or unrecoverable failure so no
 * orphaned parts are billed. Reports aggregate 0..1 progress.
 */
export const uploadMultipart = async (
  plan: MultipartPlan,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const partCount = plan.parts.length;
  const partSize = Math.ceil(file.size / partCount);
  const loaded = new Array(partCount).fill(0);
  const etags: (string | null)[] = new Array(partCount).fill(null);
  const inflight = new Set<XMLHttpRequest>();
  let cancelled = signal?.aborted ?? false;

  const report = () => {
    if (!onProgress) return;
    const sum = loaded.reduce((a, b) => a + b, 0);
    onProgress(file.size > 0 ? Math.min(1, sum / file.size) : 1);
  };
  const onAbort = () => {
    cancelled = true;
    for (const x of inflight) {
      try {
        x.abort();
      } catch {
        /* already settled */
      }
    }
  };
  signal?.addEventListener("abort", onAbort);

  const abortMultipart = () => {
    // Best-effort — free any uploaded parts so S3 doesn't retain them.
    try {
      void fetch(plan.abort.url, { method: "DELETE", headers: plan.abort.headers });
    } catch {
      /* ignore */
    }
  };

  try {
    if (cancelled) throw new DOMException("Aborted", "AbortError");

    // Bounded-concurrency part pump with per-part retry.
    let next = 0;
    const worker = async () => {
      for (;;) {
        if (cancelled) return;
        const i = next;
        if (i >= partCount) return;
        next += 1;
        const start = i * partSize;
        const blob = file.slice(start, Math.min(file.size, start + partSize));
        let ok = false;
        let lastErr = "";
        for (let attempt = 0; attempt < MULTIPART_PART_ATTEMPTS && !cancelled; attempt += 1) {
          const release = await acquirePartSlot();
          try {
            if (cancelled) throw new DOMException("Aborted", "AbortError");
            etags[i] = await putPart(
              plan.parts[i].url,
              plan.parts[i].headers,
              blob,
              (l) => {
                loaded[i] = l;
                report();
              },
              (xhr) => inflight.add(xhr),
            );
            ok = true;
            release();
            break;
          } catch (err) {
            release();
            if (cancelled) throw err;
            lastErr = err instanceof Error ? err.message : String(err);
            loaded[i] = 0;
            report();
            if (attempt < MULTIPART_PART_ATTEMPTS - 1) {
              await new Promise((r) => setTimeout(r, 500 * 2 ** attempt + Math.random() * 250));
            }
          }
        }
        // Keep the last attempt's reason (HTTP status + S3 code, "stalled", network) for the failure row.
        if (!ok) throw new Error(`Multipart part ${i + 1} failed after retries: ${lastErr || "unknown"}`);
      }
    };

    await Promise.all(Array.from({ length: Math.min(MULTIPART_CONCURRENCY, partCount) }, worker));
    if (cancelled) throw new DOMException("Aborted", "AbortError");

    // Assemble + POST the completion XML (parts in order, quoted ETags as returned).
    const xml =
      "<CompleteMultipartUpload>" +
      etags.map((e, i) => `<Part><PartNumber>${i + 1}</PartNumber><ETag>${e}</ETag></Part>`).join("") +
      "</CompleteMultipartUpload>";
    const resp = await fetch(plan.complete.url, {
      method: "POST",
      body: xml,
      headers: { "Content-Type": "application/xml", ...plan.complete.headers },
      signal,
    });
    const text = await resp.text();
    // S3 can return 200 with an <Error> body on completion failure — treat as failure.
    if (!resp.ok || /<Error>/.test(text)) {
      const code = s3Code(text);
      throw new Error(`Multipart completion failed (${resp.status})${code ? ` ${code}` : ""}`);
    }
  } catch (err) {
    if (!cancelled) abortMultipart(); // a cancel already has the caller aborting; don't double
    throw err;
  } finally {
    signal?.removeEventListener("abort", onAbort);
  }
};

/** The admin content read returns 100 hits per page (mirrors the legacy offset paging). */
export const CONTENT_PAGE_SIZE = 100;

// The burst-scan loader requests a large page so 10k–100k-photo galleries load in far fewer
// round-trips — most importantly the SEQUENTIAL cursor phase past 10k. Paired with the lean scan
// projection (projection=scan), each hit is only ~0.65 KB (one previewBase URL + a little
// metadata), so 5,000 hits ≈ 3.2 MB of JSON — under the 6 MB API Gateway/Lambda response limit.
// At 5,000/page a 100k-photo gallery is ~20 requests (~18 of them serial in the cursor phase) vs
// ~90 at 1,000/page. The server clamps the scan projection to ≤5,000, so this must stay ≤ that.
export const SCAN_PAGE_SIZE = 5000;

export interface AllContentResult {
  hits: Content[];
  /** Total photos matching the query on the server. */
  total: number;
  /** How many were actually loaded (== total, unless the cap clipped it). */
  loaded: number;
  /** True when `total` exceeded the cap, so `hits` is only the first `cap`. */
  capped: boolean;
}

/** Elasticsearch's from+size window: from-based paging can only reach the first 10,000 hits. */
const PAGE_WINDOW_MAX = 10_000;

/**
 * Load EVERY photo for a gallery/filter (up to `cap`) — the burst-scan workspace (via
 * useAllContent) and the Photos tab's "Select all" both need the whole set. Hybrid loader:
 *   1. Pages 1..N within the 10k from+size window are fetched in PARALLEL (fast) — this
 *      covers most galleries in a couple of seconds, unchanged from before.
 *   2. Beyond 10k it switches to SEQUENTIAL cursor (search_after) paging, seeded from the
 *      last windowed page's cursor, so the whole gallery loads without hitting the ES window.
 * `onProgress(loaded, wanted)` fires as pages land so the UI can show a running count. Above
 * `cap` it returns the first `cap` and flags `capped`.
 */
export const fetchAllContent = async (
  scope: TenantScope,
  query: ContentQuery,
  {
    cap = 50_000,
    signal,
    onProgress,
  }: { cap?: number; signal?: AbortSignal; onProgress?: (loaded: number, total: number) => void } = {},
): Promise<AllContentResult> => {
  // Big pages (SCAN_PAGE_SIZE) so 10k–100k-photo galleries load in far fewer round-trips.
  // The from-based phase-1 page math and the cursor phase must both use this same size.
  // projection:"scan" asks the server for the lean per-hit shape (metadata + one previewBase
  // URL instead of six), ~4× less JSON — the scan grid + shared lightbox rebuild any size.
  const sized: ContentQuery = { ...query, size: SCAN_PAGE_SIZE, projection: "scan" };
  const first = await listContent(scope, { ...sized, page: 1 }, signal);
  const total = first.total ?? first.hits.length;
  const wanted = Math.min(total, cap);
  // Highest page reachable by from-based paging (bounded by both `wanted` and the 10k window).
  const windowPages = Math.min(
    Math.ceil(Math.min(wanted, PAGE_WINDOW_MAX) / SCAN_PAGE_SIZE),
    PAGE_WINDOW_MAX / SCAN_PAGE_SIZE,
  );

  // --- Phase 1: parallel, from-based, up to the 10k window ---
  const byPage: ContentResult[] = [first];
  const pending: number[] = [];
  for (let p = 2; p <= windowPages; p += 1) pending.push(p);
  const CONCURRENCY = 6;
  let nextIdx = 0;
  const worker = async () => {
    while (nextIdx < pending.length) {
      const p = pending[nextIdx];
      nextIdx += 1;
      byPage[p - 1] = await listContent(scope, { ...sized, page: p }, signal);
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, pending.length) }, worker));

  const hits: Content[] = [];
  for (const page of byPage) if (page) hits.push(...page.hits);
  onProgress?.(Math.min(hits.length, wanted), wanted);

  // --- Phase 2: sequential cursor paging past the 10k window ---
  // Only runs when there's more to load AND the backend returned a cursor (it degrades to
  // the 10k window on an older backend that doesn't send one).
  let cursor = byPage[windowPages - 1]?.nextCursor ?? null;
  while (cursor && hits.length < wanted) {
    if (signal?.aborted) break;
    const res = await listContent(scope, { ...sized, cursor }, signal);
    if (!res.hits.length) break;
    hits.push(...res.hits);
    onProgress?.(Math.min(hits.length, wanted), wanted);
    cursor = res.nextCursor ?? null;
    if (res.hits.length < SCAN_PAGE_SIZE) break;
  }

  const clipped = hits.slice(0, cap);
  return { hits: clipped, total, loaded: clipped.length, capped: total > cap };
};

export const useAllContent = (
  scope: TenantScope,
  query: ContentQuery,
  {
    cap = 50_000,
    enabled = true,
    onProgress,
    refetchInterval,
  }: {
    cap?: number;
    enabled?: boolean;
    onProgress?: (loaded: number, total: number) => void;
    /** Re-poll cadence (react-query semantics); the uploader's remote pane polls while uploads land. */
    refetchInterval?: number | false | ((query: { state: { data?: AllContentResult } }) => number | false);
  } = {},
) =>
  useQuery({
    queryKey: [
      "content-all",
      scope.alias ?? scope.customDomain ?? "current",
      query.engineGuid ?? query.slug ?? "none",
      query.text ?? "",
      (query.names ?? []).join(","),
      JSON.stringify(query.nameSearch ?? []),
      (query.collections ?? []).join(","),
      query.requestId ?? "",
      (query.faces ?? []).join(",").slice(0, 64),
      cap,
    ],
    queryFn: ({ signal }): Promise<AllContentResult> =>
      fetchAllContent(scope, query, { cap, signal, onProgress }),
    enabled:
      enabled &&
      (!!scope.alias || !!scope.customDomain) &&
      (!!query.engineGuid || !!query.slug),
    staleTime: 30_000,
    refetchInterval,
  });

export const useContent = (
  scope: TenantScope,
  query: ContentQuery,
  enabled = true,
) =>
  useQuery({
    queryKey: [
      "content",
      scope.alias ?? scope.customDomain ?? "current",
      query.engineGuid ?? query.slug ?? "none",
      query.text ?? "",
      (query.names ?? []).join(","),
      JSON.stringify(query.nameSearch ?? []),
      (query.collections ?? []).join(","),
      query.requestId ?? "",
      (query.faces ?? []).join(",").slice(0, 64),
      query.page ?? 1,
    ],
    queryFn: ({ signal }) => listContent(scope, query, signal),
    enabled:
      enabled &&
      (!!scope.alias || !!scope.customDomain) &&
      (!!query.engineGuid || !!query.slug),
    staleTime: 30_000,
  });

// --- Photo actions ---------------------------------------------------------
// Cover/featured image + admin download. Mirrors photohawk-admin content.js.

export const setCoverImage = (scope: TenantScope, engineGuid: string, guid: string) =>
  photohawkApi.post<unknown>("/admin/engines/setCoverImage", {
    service: "admin",
    hostname: tenantHostname(scope),
    body: { engineGuid, guid },
  });

export const setFeaturedImage = (scope: TenantScope, engineGuid: string, guid: string) =>
  photohawkApi.post<unknown>("/admin/engines/setFeaturedImage", {
    service: "admin",
    hostname: tenantHostname(scope),
    body: { engineGuid, guid },
  });

// --- Watermark live preview -------------------------------------------------
// Render the current (possibly unsaved) branding + security watermark config over
// a stock landscape/portrait scene, so photographers can see where watermarks land
// without uploading a test photo. Shares the setCoverImage route (action-
// discriminated) because api-admin is at the CloudFormation 500-resource limit.

export interface WatermarkPreviewConfig {
  watermarks?: unknown[];
  mediaSecurityWatermarkGuid?: string | null;
  securityWatermarkWidth?: number;
  securityWatermarkOpacity?: number;
  securityWatermarkPosition?: string;
  securityWatermarkTiled?: boolean;
  /** The gallery's features (engine.features). When sent, the server draws Photohawk's default security
   *  watermark if no custom one is set and the gallery isn't free-download — as its previews get. */
  features?: string[];
}

export interface WatermarkPreviewResult {
  image: string; // data:image/jpeg;base64,...
  width: number;
  height: number;
}

export const previewWatermark = (
  scope: TenantScope,
  orientation: "landscape" | "portrait",
  config: WatermarkPreviewConfig,
) =>
  photohawkApi.post<WatermarkPreviewResult>("/admin/engines/setCoverImage", {
    service: "admin",
    hostname: tenantHostname(scope),
    body: { action: "watermarkPreview", orientation, config },
  });

// --- Preview protection example -------------------------------------------------
// What a buyer sees for one of the gallery's own photos under preview protection (whole person) with
// the current (possibly unsaved) watermark settings: the protected preview plus, per person, the
// layered reveal renders [frame, top half, bottom half] that the client masks into one continuous
// band. Admin-only, rendered on demand, nothing is saved.

/** The stored engine.previewProtection choice ('faces' is legacy and reads as 'person'). */
export type PreviewProtectionMode = "person" | "off";

export interface ProtectionPreviewConfig extends WatermarkPreviewConfig {
  /** Ignored by the server except 'off' — there is one mode (whole person). */
  mode: PreviewProtectionMode;
}

/** A percent box of the photo (x/width of its width, y/height of its height). */
export interface ProtectionBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ProtectionRegion extends ProtectionBox {
  /** The part of the photo the reveal renders show (their aspect = the reveal images' aspect). */
  win: ProtectionBox;
}

export interface ProtectionPreviewResult {
  /** The photo used, or null when none with people was found (see `reason`). */
  key: string | null;
  name?: string;
  mode: "person";
  width?: number;
  height?: number;
  /** data:image/jpeg;base64 — the protected preview. */
  image?: string;
  reveal?: "layers";
  /** Band height as a fraction of the person's height (0.25). */
  bandHeight?: number;
  /** Rows (fractions of the person box, 0 = top) each half image shows clear. */
  halves?: { top: [number, number]; bottom: [number, number] };
  /** The people that can be revealed (percent boxes of the photo), largest first (max 2). */
  regions?: ProtectionRegion[];
  /** Per person: [frame, top, bottom] data URLs, all the window's size. */
  reveals?: [string, string, string][];
  /** "saved" = the photo's stored scan; "live" = detected just now for this example. */
  scan?: "saved" | "live";
  reason?: "no-people" | "no-photos";
}

export const previewProtection = (
  scope: TenantScope,
  params: { engineGuid: string; key?: string; exclude?: string[]; config: ProtectionPreviewConfig },
) =>
  photohawkApi.post<ProtectionPreviewResult>("/admin/engines/setCoverImage", {
    service: "admin",
    hostname: tenantHostname(scope),
    body: { action: "protectionPreview", ...params },
  });

// --- Security-watermark overlay ---------------------------------------------
// A transparent PNG of the gallery's SECURITY watermark at the exact pixel size
// requested — the same overlay the photo pipeline lays over an image of those
// dimensions. Drawn over video frames so a downloaded clip is watermarked
// identically to the tenant's photos (no server transcode). Returned as a base64
// data URL, so drawing it onto the capture canvas does not taint it. `image` is
// null when the gallery carries no security watermark.

interface SecurityOverlayResult {
  image: string | null; // data:image/png;base64,... or null
  watermarked: boolean;
  width: number;
  height: number;
}

/** Fetch the gallery security-watermark overlay for a given frame size. Returns
 *  a PNG data URL, or null when the gallery has no security watermark. */
export const fetchSecurityOverlay = async (
  scope: TenantScope,
  engineGuid: string,
  width: number,
  height: number,
): Promise<string | null> => {
  const res = await photohawkApi.post<SecurityOverlayResult>("/admin/engines/setCoverImage", {
    service: "admin",
    hostname: tenantHostname(scope),
    body: { action: "securityOverlay", engineGuid, width, height },
  });
  return res?.image ?? null;
};

interface DownloadResponse {
  url?: string;
}

/** Delete one or more photos. DELETE /admin/content?content[]=<json {engineGuid,guid}>. */
export const deleteContent = (
  scope: TenantScope,
  items: Array<{ engineGuid: string; guid: string }>,
) =>
  photohawkApi.delete<unknown>("/admin/content", {
    service: "admin",
    hostname: tenantHostname(scope),
    params: { "content[]": items.map((i) => JSON.stringify(i)) },
  });

/** Photos per DELETE request. The ids travel in the query string (~140 bytes each), and
 *  CloudFront / API Gateway reject a URL over ~8 KB before it reaches the backend, with no CORS
 *  headers — the browser then reports only "Network Error" (a 300-photo "Select all" was a
 *  ~42 KB URL). 25 keeps the URL around 3.6 KB, and each request well inside the gateway's
 *  29 s cap even where the backend deletes one photo at a time. The backend refuses > 100. */
export const DELETE_BATCH_SIZE = 25;
/** Delete requests in flight at once. */
export const DELETE_CONCURRENCY = 3;

const PARTIAL_DELETE = "Some photos couldn’t be deleted";

export interface BulkDeleteResult {
  /** Guids the server confirmed deleted. */
  deleted: string[];
  /** Guids that were NOT deleted (safe to retry). */
  failed: string[];
  /** The first error seen, for the user-facing message. */
  error?: string;
}

/**
 * Delete any number of photos (a whole-gallery "Select all" can be tens of thousands) in
 * small batches, `concurrency` requests at a time, reporting `onProgress(processed, total)`.
 * A batch that fails transiently (network, 429, 5xx) is retried; anything the server did not
 * confirm is reported in `failed` rather than thrown, so the caller can tell the user exactly
 * how many were deleted and keep the rest selected for a retry. A 401/403 stops the run (the
 * remaining batches would fail the same way). The response of DELETE /admin/content is the
 * array of items actually removed; an item missing from it was not deleted.
 */
export const deleteContentInBatches = async (
  scope: TenantScope,
  items: Array<{ engineGuid: string; guid: string }>,
  {
    batchSize = DELETE_BATCH_SIZE,
    concurrency = DELETE_CONCURRENCY,
    retries = 2,
    onProgress,
    deleteBatch = deleteContent,
    sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms)),
  }: {
    batchSize?: number;
    concurrency?: number;
    /** Extra attempts per batch for transient failures / unconfirmed items. */
    retries?: number;
    onProgress?: (processed: number, total: number) => void;
    /** Injected for tests. */
    deleteBatch?: (scope: TenantScope, items: Array<{ engineGuid: string; guid: string }>) => Promise<unknown>;
    sleep?: (ms: number) => Promise<void>;
  } = {},
): Promise<BulkDeleteResult> => {
  // De-duplicate by guid (a selection is a Set, but be defensive).
  const unique = Array.from(new Map(items.map((i) => [i.guid, i])).values());
  const batches: Array<typeof unique> = [];
  for (let i = 0; i < unique.length; i += batchSize) batches.push(unique.slice(i, i + batchSize));

  const deleted: string[] = [];
  const failed: string[] = [];
  let error: string | undefined;
  let stopped = false;
  let processed = 0;
  const total = unique.length;
  onProgress?.(0, total);

  const runBatch = async (batch: typeof unique) => {
    let remaining = batch;
    for (let attempt = 0; attempt <= retries && remaining.length && !stopped; attempt += 1) {
      if (attempt > 0) await sleep(1000 * attempt);
      try {
        const res = await deleteBatch(scope, remaining);
        // An array lists exactly what was removed; anything else (older backend / no body)
        // means the request as a whole succeeded.
        const removed = Array.isArray(res)
          ? new Set(res.map((r) => String((r as { guid?: unknown })?.guid ?? "")))
          : new Set(remaining.map((r) => r.guid));
        for (const r of remaining) if (removed.has(r.guid)) deleted.push(r.guid);
        remaining = remaining.filter((r) => !removed.has(r.guid));
        if (remaining.length && !error) error = PARTIAL_DELETE;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (!error || error === PARTIAL_DELETE) error = msg;
        const status = e instanceof PhotohawkApiError ? e.status : 0;
        if (status === 401 || status === 403) {
          stopped = true;
          break;
        }
        if (!isTransientApiError(e)) break;
      }
    }
    failed.push(...remaining.map((r) => r.guid));
    processed += batch.length;
    onProgress?.(processed, total);
  };

  let next = 0;
  const worker = async () => {
    while (next < batches.length) {
      const batch = batches[next];
      next += 1;
      if (stopped) {
        failed.push(...batch.map((r) => r.guid));
        processed += batch.length;
        onProgress?.(processed, total);
        continue;
      }
      await runBatch(batch);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, batches.length)) }, worker));

  return { deleted, failed, ...(failed.length ? { error: error ?? PARTIAL_DELETE } : {}) };
};

/** Hide (or un-hide) content from PUBLIC gallery search (PUT /admin/content).
 *  `guids` = photos; `videoHides` = matched frame ranges to hide per video;
 *  `videoGuids` = whole videos to un-hide (clear their ranges). Drives the
 *  saved-search Profiles "hide matches" flow. */
export const setContentHidden = (
  scope: TenantScope,
  input: {
    guids?: string[];
    videoHides?: Array<{ videoGuid: string; from: number; to: number }>;
    videoGuids?: string[];
    hidden: boolean;
  },
) =>
  photohawkApi.put<{ updated?: number; videosUpdated?: number }>("/admin/content", {
    service: "admin",
    hostname: tenantHostname(scope),
    body: {
      guids: input.guids ?? [],
      videoHides: input.videoHides ?? [],
      videoGuids: input.videoGuids ?? [],
      hidden: input.hidden,
    },
  });

/** A crop region as PERCENTAGES (0–100) of the original — react-easy-crop's
 *  `croppedArea` shape, which the backend's sharp-based cropper expects. */
export interface DownloadCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Generate an admin download URL for a single photo. Pass `watermark: true` to
 * get the watermarked render (the backend applies the gallery's branding /
 * security watermarks in downloadContent). Pass `crop` (percentages) to have the
 * backend return the cropped render — the same server-side crop the customer was
 * delivered — with `rotation` applied first when set. Omit `crop` for the full
 * original frame.
 */
export const adminDownload = async (
  scope: TenantScope,
  engineGuid: string,
  contentGuid: string,
  watermark = false,
  crop?: DownloadCrop | null,
  rotation?: number,
): Promise<string> => {
  const res = await photohawkApi.post<DownloadResponse>("/admin/content/download", {
    service: "admin",
    hostname: tenantHostname(scope),
    body: {
      engineGuid,
      contentGuid,
      watermark,
      // Only send crop/rotation when a crop is requested, so an ordinary
      // download stays on the fast no-crop path (a temp S3 URL of the original).
      ...(crop ? { crop, rotation: rotation ?? 0 } : {}),
    },
  });
  if (!res?.url) throw new Error("No download URL returned");
  return res.url;
};
